import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Upload, Tooltip, Spin, message, Popconfirm } from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  UploadOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  upload_company_documents,
  getPoolDocuments,
} from "../../features/Pooling/PoolingSlice";
import { makeRequest } from "../../api/api";

const DOC_DEFINITIONS = [
  { name: "KYC", key: "kyc" },
  { name: "Term Sheet", key: "term_sheet" },
  { name: "Contract", key: "contract" },
  { name: "Invoice", key: "invoice" },
  { name: "Payment Reference", key: "payment_reference" },
];

const DOC_TYPE_MAP = {
  kyc: "kyc_files",
  term_sheet: "term_sheet_files",
  contract: "contract_files",
  invoice: "invoice_files",
  payment_reference: "payment_reference_files",
};

const DOC_KEYS = Object.keys(DOC_TYPE_MAP);

const createEmptyDocMap = () =>
  DOC_KEYS.reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {});

const createEmptyStagedFilesMap = () =>
  DOC_KEYS.reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {});

const getFrontendKeyFromDocType = (docType) =>
  DOC_KEYS.find((key) => DOC_TYPE_MAP[key] === docType);

const UploadDocumentsModal = ({ visible, onCancel, poolId, companyName, viewOnly }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.pooling);

  const docs = DOC_DEFINITIONS;

  const [companyDocs, setCompanyDocs] = useState(() => createEmptyDocMap());
  const [stagedFiles, setStagedFiles] = useState(() => createEmptyStagedFilesMap());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasPoolDocsRecord, setHasPoolDocsRecord] = useState(false);
  const [error, setError] = useState(null);

  // Validate props
  useEffect(() => {
    if (visible) {
      console.log("Modal opened with props:", { poolId, companyName, viewOnly });
      if (!poolId) {
        console.error("poolId is required");
        setError("Pool ID is missing");
      }
      if (!companyName) {
        console.error("companyName is required");
        setError("Company name is missing");
      }
    }
  }, [visible, poolId, companyName, viewOnly]);

  const resetStagedFiles = useCallback(() => {
    setStagedFiles(createEmptyStagedFilesMap());
  }, []);

  const fetchExistingDocs = useCallback(async () => {
    if (!poolId) {
      setCompanyDocs(createEmptyDocMap());
      setHasPoolDocsRecord(false);
      return;
    }

    try {
      const result = await dispatch(
        getPoolDocuments({ poolId, companyName })
      );

      console.log("fetchExistingDocs result:", result);

      if (result.meta.requestStatus === "fulfilled") {
        const documents = result.payload?.documents || [];
        const mappedDocs = createEmptyDocMap();

        console.log("Documents from API:", documents);

        documents.forEach((item) => {
          if (!item || typeof item !== 'object') return;

          // API uses 'key' field (e.g., "kyc_files")
          const docType = item.key;
          const frontendKey = getFrontendKeyFromDocType(docType);

          if (!frontendKey) {
            console.warn("No frontend key for doc_type:", docType);
            return;
          }

          // API returns 'url' field with full S3 URL
          const storagePath = item.url || "";

          if (!storagePath) {
            console.warn("No URL for doc:", frontendKey);
            return;
          }

          mappedDocs[frontendKey] = {
            url: storagePath,
            storagePath,
            fileName: storagePath.split('/').pop() || "",
            docType,
          };
        });

        console.log("Mapped docs:", mappedDocs);
        setCompanyDocs(mappedDocs);
        setHasPoolDocsRecord(documents.length > 0);
      } else {
        console.warn("Request not fulfilled:", result);
        setCompanyDocs(createEmptyDocMap());
        setHasPoolDocsRecord(false);
      }
    } catch (error) {
      console.error("Failed to fetch pool documents", error);
      console.error("Error stack:", error.stack);
      setCompanyDocs(createEmptyDocMap());
      setHasPoolDocsRecord(false);
    }
  }, [dispatch, poolId, companyName]);

  useEffect(() => {
    if (!visible) {
      resetStagedFiles();
      return;
    }

    fetchExistingDocs();
  }, [visible, resetStagedFiles, fetchExistingDocs]);

  const handleFileSelect = (docType, file) => {
    setStagedFiles((prev) => ({
      ...prev,
      [docType]: file,
    }));
    message.success(`${file.name} ready to upload`);
    return false;
  };

  const handleSubmitAll = async () => {
    const allDocsPresent = docs.every(
      (doc) => companyDocs[doc.key] || stagedFiles[doc.key]
    );

    if (!allDocsPresent) {
      message.error("Please upload all required documents");
      return;
    }

    setIsSubmitting(true);

    try {
      const filesToProcess = docs.filter((doc) => stagedFiles[doc.key]);

      if (filesToProcess.length === 0) {
        message.info("No new documents selected");
        setIsSubmitting(false);
        return;
      }

      let successCount = 0;
      let successKeys = [];
      let failures = [];

      const processWithPatch = async () => {
        const outcome = { successCount: 0, successKeys: [], failures: [] };

        for (const doc of filesToProcess) {
          const fileToSend = stagedFiles[doc.key];
          if (!fileToSend) continue;

          const docTypeValue = DOC_TYPE_MAP[doc.key];

          const patchFormData = new FormData();
          patchFormData.append("pool_id", poolId);
          
          // WORKAROUND: Backend bug - it needs company_name even though the error says it doesn't support it
          if (companyName) {
            patchFormData.append("company_name", companyName);
          }
          
          patchFormData.append("doc_type", docTypeValue);
          patchFormData.append("action", "replace");
          patchFormData.append("file", fileToSend);

          console.log(`Processing ${doc.key} via PATCH...`);

          try {
            const response = await makeRequest(
              "patch",
              `v2/pooling/update_pool_document/`,
              patchFormData
            );

            if (response && response.data) {
              outcome.successCount++;
              outcome.successKeys.push(doc.key);
              console.log(`${doc.key} updated successfully`);
            } else {
              outcome.failures.push(`${doc.name}: No response data`);
            }
          } catch (err) {
            console.error(`PATCH failed for ${doc.key}:`, err.response?.data);
            const errorMsg = err.response?.data?.error || err.message || "Unknown error";
            outcome.failures.push(`${doc.name}: ${errorMsg}`);
          }
        }

        return outcome;
      };

      // Check if we need to create initial record or update existing
      if (!hasPoolDocsRecord) {
        // Initial upload - use POST with all files
        const creationFormData = new FormData();
        creationFormData.append("pool_id", poolId);
        if (companyName) {
          creationFormData.append("company_name", companyName);
        }

        filesToProcess.forEach((doc) => {
          const backendKey = DOC_TYPE_MAP[doc.key];
          const fileToSend = stagedFiles[doc.key];
          if (fileToSend) {
            creationFormData.append(backendKey, fileToSend);
          }
        });

        try {
          const creationResult = await dispatch(upload_company_documents(creationFormData));

          if (creationResult.meta.requestStatus === "fulfilled") {
            successCount = filesToProcess.length;
            successKeys = filesToProcess.map((doc) => doc.key);
            setHasPoolDocsRecord(true);
          } else {
            const creationError =
              creationResult.payload?.error ||
              creationResult.error?.message ||
              "Unknown error";

            // If it says already exists, switch to PATCH
            const normalized = (creationError || "").toLowerCase();
            if (normalized.includes("already") || normalized.includes("exist")) {
              setHasPoolDocsRecord(true);
              const patchOutcome = await processWithPatch();
              successCount = patchOutcome.successCount;
              successKeys = patchOutcome.successKeys;
              failures = patchOutcome.failures;
            } else {
              failures.push(`Create documents: ${creationError}`);
            }
          }
        } catch (err) {
          failures.push(`Create documents: ${err.message || "Unknown error"}`);
        }
      } else {
        // Update existing - use PATCH for each file
        const patchOutcome = await processWithPatch();
        successCount = patchOutcome.successCount;
        successKeys = patchOutcome.successKeys;
        failures = patchOutcome.failures;
      }

      // Handle results
      if (successCount > 0) {
        message.success(`${successCount} document(s) uploaded successfully`);
        
        // Refresh document list
        await fetchExistingDocs();

        // Clear staged files that were successfully uploaded
        setStagedFiles((prev) => {
          const updated = { ...prev };
          successKeys.forEach((key) => {
            updated[key] = null;
          });
          return updated;
        });

        // Close modal if all succeeded
        if (failures.length === 0) {
          onCancel();
        }
      }

      if (failures.length > 0) {
        const errorText = failures.join(", ");
        if (successCount > 0) {
          message.warning(`Some documents failed: ${errorText}`);
        } else {
          message.error(`Upload failed: ${errorText}`);
        }
      }
    } catch (e) {
      console.error("Upload error:", e);
      message.error("Upload failed: " + (e.message || "Unknown error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreview = async (docKey) => {
    // Preview staged file
    if (stagedFiles[docKey]) {
      const objectUrl = URL.createObjectURL(stagedFiles[docKey]);
      const previewWindow = window.open(objectUrl, "_blank");
      if (!previewWindow) {
        message.error("Please allow popups to preview documents");
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60 * 1000);
      return;
    }

    // Preview uploaded file
    const docMeta = companyDocs[docKey];

    if (!docMeta || !docMeta.url) {
      message.warning("No document available to preview");
      return;
    }

    const previewWindow = window.open(docMeta.url, "_blank");
    if (!previewWindow) {
      message.error("Please allow popups to preview documents");
    }
  };

  const handleDeleteAll = async () => {
    if (!poolId || !companyName) {
      message.error("Missing pool ID or company name");
      return;
    }

    setIsDeleting(true);

    try {
      const response = await makeRequest(
        "delete",
        `v2/pooling/delete_all_pool_documents/`,
        {
          pool_id: poolId,
          company_name: companyName,
        }
      );

      if (response && response.data) {
        message.success("All documents deleted successfully");
        
        // Clear all state
        setCompanyDocs(createEmptyDocMap());
        setStagedFiles(createEmptyStagedFilesMap());
        setHasPoolDocsRecord(false);
        
        // Optionally close modal
        onCancel();
      } else {
        message.error("Failed to delete documents");
      }
    } catch (err) {
      console.error("Delete error:", err);
      const errorMsg = err.response?.data?.error || err.message || "Failed to delete documents";
      message.error(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusIcon = (docKey) => {
    const hasUploaded = Boolean(companyDocs[docKey]);
    const isStaged = Boolean(stagedFiles[docKey]);

    if (hasUploaded) {
      return (
        <>
          <CheckCircleTwoTone twoToneColor="#52c41a" />
          <span style={{ marginLeft: 5 }}>Uploaded</span>
        </>
      );
    }

    if (isStaged) {
      return (
        <>
          <CheckCircleTwoTone twoToneColor="#faad14" />
          <span style={{ marginLeft: 5, color: "#faad14" }}>Ready</span>
        </>
      );
    }

    return (
      <>
        <CloseCircleTwoTone twoToneColor="#ff4d4f" />
        <span style={{ marginLeft: 5 }}>Missing</span>
      </>
    );
  };

  const allDocsPresent = docs.every(
    (doc) => companyDocs[doc.key] || stagedFiles[doc.key]
  );

  const hasAnyUploadedDocs = docs.some((doc) => companyDocs[doc.key]);

  return (
    <Modal
      title={<h2>{viewOnly ? "View Documents" : "Upload Documents"} – {companyName || "Unknown"}</h2>}
      open={visible}
      onCancel={onCancel}
      destroyOnClose={false}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={isSubmitting || isDeleting}>
          {viewOnly ? "Close" : "Cancel"}
        </Button>,
        !viewOnly && hasAnyUploadedDocs && (
          <Popconfirm
            key="delete"
            title="Delete all documents?"
            description="This will permanently delete all uploaded documents for this company. This action cannot be undone."
            onConfirm={handleDeleteAll}
            okText="Yes, delete all"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={isDeleting}
              disabled={isSubmitting || isDeleting}
            >
              Delete All
            </Button>
          </Popconfirm>
        ),
        !viewOnly && (
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmitAll}
            disabled={!allDocsPresent || isSubmitting || isDeleting || !!error}
            loading={isSubmitting}
          >
            Submit All
          </Button>
        ),
      ]}
    >
      {error ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "red" }}>
          <p>Error: {error}</p>
          <Button onClick={() => setError(null)}>Retry</Button>
        </div>
      ) : loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <p style={{ color: "gray", marginBottom: 20 }}>
            {viewOnly
              ? `View uploaded documents for ${companyName || "this company"}.`
              : `Upload all required documents for ${companyName || "this company"}.`
            }
          </p>

          <div style={{ borderTop: "1px solid #f0f0f0" }}>
            {docs.map((doc) => (
              <div
                key={doc.key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div style={{ fontWeight: 500 }}>
                  {doc.name}
                  {stagedFiles[doc.key] && !viewOnly && (
                    <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>
                      ({stagedFiles[doc.key].name})
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {!viewOnly && (
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => handleFileSelect(doc.key, file)}
                      accept=".pdf,.doc,.docx"
                    >
                      <Button icon={<UploadOutlined />}>
                        {stagedFiles[doc.key] ? "Change" : "Select"}
                      </Button>
                    </Upload>
                  )}

                  {(companyDocs[doc.key] || stagedFiles[doc.key]) && (
                    <Tooltip title="Open in new tab">
                      <Button
                        icon={<EyeOutlined />}
                        onClick={() => handlePreview(doc.key)}
                      />
                    </Tooltip>
                  )}

                  {getStatusIcon(doc.key)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
};

export default UploadDocumentsModal;